import clsx from 'clsx';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { EventsFilterContext } from '../../../context/eventsFIlterContext';
import { ADD_FILTER } from '../../../reducer/eventsFilterReducer';
import { getEventControllers } from '../../../service/eventsApi';
import { EVENT_FILTER_COMPONENT_ORDER, EVENT_FILTER_COMPONENT_PAGINATION, EVENT_FILTER_COMPONENTS } from '../../../utility/constants';
import { getEventInitialLabel, getInitialEventFilterValues } from '../../../utility/event';
import Chip from '../chip';
import Dropdown from '../drop-down';
import useStyles from './styles';
import { useTranslation } from 'react-i18next';

const Controllers = (props) => {
  const { overflowItems, chipRefs } = props;
  
  const { t } = useTranslation();
  
  const { state: stateFilter, dispatch } = useContext(EventsFilterContext);
  
  const name = EVENT_FILTER_COMPONENTS.CONTROLLERS;
  const componentOrder = EVENT_FILTER_COMPONENT_ORDER.indexOf(name);
  
  const { deviceIds: deviceIdsState = [] } = stateFilter;

  const initialControllers = getInitialEventFilterValues(deviceIdsState);

  const defaultLabel = t(`events-page-filter.${name}`);
  const initialLabel = getEventInitialLabel(initialControllers, defaultLabel, t('events-page-filter.multipleControllersLabel', { count: initialControllers?.length }));

  const initialList = { parent: null, items: [], isMain: true };

  const classes = useStyles();
  const [status, setStatus] = useState(initialControllers.length);
  const [selectedValues, setSelectedValues] = useState(initialControllers);
  const [list, setList] = useState(initialList);
  const [label, setLabel] = useState(initialLabel);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const handleDelete = () => {
    setSelectedValues([]);
    setLabel(defaultLabel);
    dispatch({ type: ADD_FILTER, payload: {...stateFilter, deviceIds: [] } })
    setStatus(false);
  };

  const handleSelect = (value, children = []) => {
    let updatedSelections = [...selectedValues];
    const isValueSelected = updatedSelections.find(item => value.id === item.id)
    
    if (isValueSelected) {
      updatedSelections = updatedSelections.filter(item => item.id !== value.id);
    } else {
      updatedSelections.push(value);
    }
    
    setSelectedValues(updatedSelections);
  };  

  const handleSearch = (value) => {
    setKeyword(value);
    setPage(0);
  };

  const handleList = (item) => {
    setList(item);
  }

  const handleClose = () => {
    const resetControllers = getInitialEventFilterValues(deviceIdsState);
    const newLabel = getEventInitialLabel(resetControllers, defaultLabel, t('events-page-filter.multipleControllersLabel', { count: resetControllers?.length }));
    
    setSelectedValues(resetControllers);
    setLabel(newLabel);
    setStatus(!!resetControllers.length);
  }

  const isArrayLocationsEqual = (selectedLocations, prevLocations) => {
    if (selectedLocations.length !== prevLocations.length) {
      return false;
    }

    return selectedLocations.every((location, index) => location.id === prevLocations[index].id);
  };

  const handleSubmit = () => {
    const hasChanged = !isArrayLocationsEqual(selectedValues, initialControllers);
    if (!hasChanged) {
      return;
    }
    const deviceIds = selectedValues.map(value => {
      return {
        id: value.id, 
        label: value.label
      }
    });

    dispatch({ type: ADD_FILTER, payload: {...stateFilter, deviceIds } })

    let newLabel = defaultLabel;
    if (selectedValues?.length > 1) {
      newLabel = t('events-page-filter.multipleControllersLabel', { count: selectedValues?.length });
    } else if (selectedValues.length === 1) {
      newLabel = selectedValues[0]?.label; 
    }

    setLabel(newLabel);
    setStatus(hasChanged);
  };

  const getData = useCallback(async() => {
    setIsLoading(true);
    try {
      const parameters = {
        keyword,
        page,
        size: EVENT_FILTER_COMPONENT_PAGINATION.SIZE
      }
      const response = await getEventControllers(parameters);
      const { _embedded, page: pagination } = response.data;
      const { totalElements, totalPages, number } = pagination;
      const controllers = _embedded.devices.map((device) => {
        return {
          id      : device,
          label   : device,
          children: []
        }
      });

      setList(prev => {
        const newList = number === 0 ? controllers : [...prev.items, ...controllers];
        return { 
          parent: null, 
          items: newList, 
          isMain: true 
        }
      });
      setHasMore(totalElements > 0 && page < totalPages - 1);

    } catch(e) {
      console.log('Error', e)
    } finally {
      setIsLoading(false);
    }
  }, [keyword, page]);

  const handleScroll = () => {
    setPage((prev) => prev + 1);
  }

  useEffect(() => {
    let delayDebounce;

    if (keyword) {
      delayDebounce = setTimeout(() => {
        getData();
      }, 500);
    } else {
      getData();
    }

    return () => {
      delayDebounce && clearTimeout(delayDebounce);
    }
  }, [keyword, page, getData]);

  useEffect(() => {
    setSelectedValues(deviceIdsState);
    let initialLabel = getEventInitialLabel(deviceIdsState, defaultLabel, t('events-page-filter.multipleControllersLabel', { count: deviceIdsState?.length }));
    setLabel(initialLabel);
    setStatus(deviceIdsState.length);
  }, [deviceIdsState, defaultLabel, t]);

  return (
    <Chip
      handleDelete={handleDelete}
      showCloseIcon={status}
      containerStyle={clsx(
        !status ? classes.inactiveChip : '',
        overflowItems?.includes(componentOrder) ? classes.hiddenChip : '',
        !chipRefs && classes.overflow
      )}
      status={selectedValues.length}
      ref={(ref) => {
        if (ref && chipRefs) chipRefs.current[componentOrder] = ref;
      }}
    >
      <Dropdown
        label={label}
        list={list}
        handleSubmit={handleSubmit}
        handleSearch={handleSearch}
        handleScroll={handleScroll}
        backLabel=''
        handleSelect={handleSelect}
        selectedValues={selectedValues}
        handleList={handleList}
        handleClose={handleClose}
        isLoading={isLoading}
        hasMore={hasMore}
      />
    </Chip>
  );
};

export default Controllers;
